'use client';
import { IconCross } from '@/icons/IconCross';
import IComponentProps from '@/interfaces/componentProps';
import { Transition } from '@headlessui/react';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const Modal = ({
  open,
  heading,
  className = `w-[50rem] ${heading ? '' : 'px-2 py-3'}`,
  closeModal,
  children,
  closeButton = true,
  changeHeight = true,
  contentClass,
  adPopUp = false,
  roundedCloseButton = false,
  dialogStyles,
  dialogClasses,
  bodyClassName,
  headingClassName,
}: {
  open: boolean;
  heading?: string;
  closeModal: () => void;
  closeButton?: boolean;
  changeHeight?: boolean;
  contentClass?: string;
  adPopUp?: boolean;
  roundedCloseButton?: boolean;
  dialogStyles?: React.CSSProperties;
  dialogClasses?: string;
  bodyClassName?: string;
  headingClassName?: string;
} & IComponentProps) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (!open) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [open]);

  return mounted ? (
    createPortal(
      <>
        <Transition show={open}>
          <div className={`fixed inset-0 z-[201] flex h-full w-full bg-transparent`} onClick={closeModal}>
            <Transition
              appear={true}
              as={'div'}
              className="absolute inset-0 flex h-full w-full bg-black bg-opacity-20"
              enter="transition-opacity duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-all duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            ></Transition>
            <Transition
              as={'div'}
              className={`relative z-[101] m-auto min-w-[90%] max-w-[1123px] p-8 md:min-w-[400px] ${contentClass}`}
              enter="transition-all duration-300"
              enterFrom="max-xs:translate-y-full xs:scale-0"
              enterTo="max-xs:translate-y-0 xs:scale-100"
              leave="transition-all duration-300"
              leaveFrom="max-xs:translate-y-0 xs:scale-100"
              leaveTo="max-xs:translate-y-full xs:scale-0"
            >
              <div
                onClick={(event: any) => event.stopPropagation()}
                className={`modal-dialog w-full overflow-hidden rounded-xl bg-white ${!adPopUp && 'pb-8 xs:pb-4'} ${dialogClasses ?? ''}`}
                style={{ boxShadow: '0px 0px 20px rgb(0 0 0 / 50%)', ...dialogStyles }}
              >
                <div className={`relative flex max-h-[90vh] flex-col rounded-[inherit] ${className}`}>
                  {(heading || closeButton) && (
                    <div className={`modal-heading flex text-lg ${heading ? 'relative border-b' : ''}`}>
                      {heading && (
                        <h4 className={`flex-grow py-5 pl-8 pr-20 font-semibold leading-none ${headingClassName ?? ''}`}>{heading}</h4>
                      )}
                      {closeButton && (
                        <span
                          className={`absolute top-0 right-0 z-[1000000] ml-auto ${adPopUp ? 'rounded-full bg-white shadow-lg' : ''} ${
                            roundedCloseButton ? 'm-3 rounded-full bg-gray-100 shadow-sm' : ''
                          }`}
                        >
                          <IconCross
                            onClick={closeModal}
                            className={`cursor-pointer text-gray-700 ${roundedCloseButton ? 'w-8 p-2.5' : 'w-8 p-1.5'} ${
                              heading && !roundedCloseButton ? 'mr-4 mt-4' : 'm-1'
                            }`}
                          />
                        </span>
                      )}
                    </div>
                  )}
                  <div
                    className={`modal-body overflow-x-hidden overflow-y-auto ${!adPopUp && 'px-8 py-6'} text-xl ${bodyClassName ?? ''}`}
                    style={changeHeight ? { maxHeight: heading || closeButton ? `calc(100vh - 10rem)` : `calc(100vh - 4rem)` } : {}}
                  >
                    {children}
                  </div>
                </div>
              </div>
            </Transition>
          </div>
        </Transition>
      </>,
      document.getElementById('portal')!,
    )
  ) : (
    <></>
  );
};

export default Modal;
